import { BRAND_LOGO, PortalShell } from '@beautyburst/ui';
import type { ReactNode } from 'react';
import type { AdminActor } from './api-client.ts';
import { ADMIN_ROUTES, resolveAdminRoute, type RouteContract } from './routes.ts';
import { adminAsset, adminHref } from './base-path.ts';

export function App({
  path,
  actor,
  renderScreen,
  flags = {},
  logoSrc = adminAsset(BRAND_LOGO.mark),
}: {
  readonly path: string;
  readonly actor: AdminActor;
  /**
   * Enabled feature flags from the administrator bootstrap. A screen gated by a
   * flag must not appear in navigation or render while that flag is off:
   * advertising an unavailable screen discloses unreleased functionality and
   * leads an administrator to a dead end.
   */
  readonly flags?: Readonly<Record<string, boolean>>;
  readonly renderScreen: (route: RouteContract) => ReactNode;
  /** Founder-supplied logo asset. Defaults to the approved artwork. */
  readonly logoSrc?: string;
}) {
  const flagAllows = (item: RouteContract) =>
    item.flag === undefined || item.flag === null || flags[item.flag] === true;
  const resolved = resolveAdminRoute(path);
  const route = resolved !== undefined && flagAllows(resolved) ? resolved : undefined;
  const permitted = ADMIN_ROUTES.filter(
    (item) => actor.permissions.includes(item.permission ?? '') && flagAllows(item),
  );
  const current = route?.path ?? path;
  return (
    <PortalShell
      surface="admin"
      title={route?.screen ?? 'Administrator console'}
      logoSrc={logoSrc}
      nav={permitted.map((item) => ({
        key: item.path,
        label: item.screen,
        href: adminHref(item.path),
        current: item.path === current,
      }))}
    >
      {route === undefined ? (
        <section aria-labelledby="not-found-heading">
          <h2 id="not-found-heading">Screen unavailable</h2>
          <p>The requested administrator screen is not part of this console.</p>
        </section>
      ) : (
        renderScreen(route)
      )}
    </PortalShell>
  );
}
